import React, { Fragment, useContext, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Modal from 'components/modal/Modal';
import { Button, ButtonsGroup } from 'components/button';
import { CardBody, CardFooter } from 'components/card';
import {
  CardHeader,
  CardHeaderTitle,
  CardHeaderSubtitle
} from 'components/card/card-header';
import {
  copyPersonToOtherEntity,
  movePersonToOtherEntity,
  addPersonToEntityFromPool
} from 'services/aws/client-query';
import messages from 'messages';
import { Table, TableBody, TableRow, TableCell } from 'components/table';
import { StoreContext } from 'index';
import Loader from 'components/loader/Loader';
import SelectGroupsCard from 'containers/partials/cards/SelectGroupsCard';
import { useEntityContext } from 'containers/pages/persons/store/useEntityContext';
import { generatePath } from 'react-router-dom';

export const PERSON_ACTION = Object.freeze({
  COPY: 'copy',
  MOVE: 'move',
  COPY_POOL: 'copyPool'
});

const CopyPersonsModal = ({
  action,
  selected = [],
  excludedGroups = [],
  oldEntityId,
  onClose,
  onDone
}) => {
  const {
    authStore: {
      user: { rootEntityId }
    },
    uiState
  } = useContext(StoreContext);
  const intl = useIntl();

  const selectedGroups = useEntityContext(s => s.selectedGroups);

  const [errors, setErrors] = useState([]);
  const [errorState, setErrorState] = useState(false);
  const [responses, setResponses] = useState([]);

  const doModalAction = async selected => {
    const newEntityId = selectedGroups[0];
    let mutation;
    let mutationVariables;

    // eslint-disable-next-line default-case
    switch (action) {
      case PERSON_ACTION.COPY:
        mutation = copyPersonToOtherEntity;
        mutationVariables = {
          oldEntityId,
          newEntityId: newEntityId.id
        };
        break;
      case PERSON_ACTION.MOVE:
        mutation = movePersonToOtherEntity;
        mutationVariables = {
          oldEntityId,
          newEntityId: newEntityId.id
        };
        break;
      case PERSON_ACTION.COPY_POOL:
        mutation = addPersonToEntityFromPool;
        mutationVariables = {
          topLevelEntityId: oldEntityId,
          newEntityId: newEntityId.id
        };
        break;
    }

    uiState.increasePendingRequest();
    const responses = [];
    for (const person of selected) {
      responses.push(
        await mutation({
          ...mutationVariables,
          personId: person.id ?? person
        })
      );
    }
    uiState.decreasePendingRequest();
    const errors = responses.filter(resp => resp.error);
    const target = generatePath('/persons/:entityId/:groupId', {
      entityId: rootEntityId,
      groupId: newEntityId.id
    });
    if (errors.length === 0) {
      onDone && onDone([oldEntityId, newEntityId.id], target);
    } else {
      setErrorState(true);
      setErrors(errors);
      setResponses(responses);
      if (errors.length !== responses.length) {
        // await actions.refetch(oldEntityId);
        // await actions.refetch(newEntityId.id);
      }
    }
  };

  const onRetry = () => {
    const errorPersons = errors.map(e =>
      selected.find(s => s.id === e.personId)
    );
    doModalAction(errorPersons);
  };

  const intlFailedLabel =
    action === PERSON_ACTION.COPY || action === PERSON_ACTION.COPY_POOL
      ? messages.modalCopySporterFailed
      : messages.modalMoveSporterFailed;

  const intlHasErrorsLabel =
    action === PERSON_ACTION.COPY || action === PERSON_ACTION.COPY_POOL
      ? messages.modalCopySporterHasErrors
      : messages.modalMoveSporterHasErrors;

  return (
    <Modal
      card
      isOpen={true}
      onClose={onClose}
      extraClassNames={'c-card-modal__content-body-scroll'}
    >
      {uiState.pendingRequestCount > 0 && <Loader />}
      <CardHeader
        modal
        extraClassNames="u-padding-bottom-medium c-card__header--long-content"
      >
        <div className="c-card__header-wrapper">
          <CardHeaderTitle extraClassNames="u-margin-bottom-small">
            {intl.formatMessage(messages.modalCopyOrMoveSporterTitle, {
              copy:
                action === PERSON_ACTION.COPY ||
                action === PERSON_ACTION.COPY_POOL
            })}
          </CardHeaderTitle>
          <CardHeaderSubtitle>
            {intl.formatMessage(messages.modalCopyOrMoveSporterSubtitle, {
              copy:
                action === PERSON_ACTION.COPY ||
                action === PERSON_ACTION.COPY_POOL,
              count: selected.length,
              listAthletes: selected
                .map(s => `${s.firstname} ${s.lastname}`.trim())
                .join(', '),
              b: chunks => <b>{chunks}</b>,
              selectedGroupsList: selectedGroups
                .map(g => `${g.name}`.trim())
                .join(', '),
              selectedGroupsLength: selectedGroups.length
            })}
          </CardHeaderSubtitle>
        </div>
      </CardHeader>
      {!errorState && (
        <Fragment>
          <CardBody modal bodyWithHeader>
            <SelectGroupsCard
              entityId={rootEntityId}
              showOnlyGroups={true}
              excludedGroups={excludedGroups}
              onModal
              showSearch
              extraClassNamesForList={'u-padding-top-none'}
            />
          </CardBody>
          <CardFooter modal>
            <ButtonsGroup>
              <Button secondary onClick={onClose}>
                <FormattedMessage {...messages.buttonCancel} />
              </Button>
              <Button
                primary
                disabled={selectedGroups.length === 0}
                onClick={() => doModalAction(selected)}
              >
                <FormattedMessage
                  {...messages.modalButtonCopyOrMove}
                  values={{
                    copy:
                      action === PERSON_ACTION.COPY ||
                      action === PERSON_ACTION.COPY_POOL,
                    count: selected.length
                  }}
                />
              </Button>
            </ButtonsGroup>
          </CardFooter>
        </Fragment>
      )}
      {errorState && (
        <Fragment>
          <CardBody modal bodyWithHeader>
            <div className="c-file-upload--wrapper-states c-file-upload--wrapper-states-errors">
              <p>
                <span className="u-color-error">
                  {errors.length === responses.length
                    ? intl.formatMessage(intlFailedLabel)
                    : intl.formatMessage(intlHasErrorsLabel, {
                        count: errors.length
                      })}
                </span>
              </p>
              <Table errorList>
                <TableBody>
                  {errors.map((p, i) => {
                    const person = selected.find(s => s.id === p.personId);
                    const errorMessage =
                      p.error.message &&
                      p.error.message.search('person_already_in_new_entity') !==
                        -1
                        ? messages.modalCopyMoveSporterErrorExistingSporter
                        : messages.modalCopyMoveSporterErrorUnknownReason;
                    return (
                      <TableRow key={i}>
                        <TableCell>{`${person.firstname} ${person.lastname}`}</TableCell>
                        <TableCell>
                          <span className="u-color-error">
                            {intl.formatMessage(errorMessage)}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardBody>
          <CardFooter modal>
            <ButtonsGroup fullwidth>
              <Button secondary onClick={() => setErrorState(null)}>
                <FormattedMessage {...messages.buttonCancel} />
              </Button>
              <Button primary onClick={onRetry}>
                <FormattedMessage
                  {...messages.modalButtonRetry}
                  values={{ count: errors.length }}
                />
              </Button>
            </ButtonsGroup>
          </CardFooter>
        </Fragment>
      )}
    </Modal>
  );
};

export default CopyPersonsModal;
